import React from "react";
import type { ComponentType } from "react";
import { Route } from "react-router-dom";

import PrivateRoute from "components/PrivateRoute";

/**
 * Shape of the dashboard route-config objects produced in
 * `pages/dashboards/Routes` and consumed here / by `useDashboardRoutes`. Only
 * the fields this layer reads are modeled.
 */
export interface DashboardRoute {
  path: string;
  layout: string;
  // required: every route this layer renders passes `component` to
  // PrivateRoute unconditionally (collapse parents recurse via `views` and
  // never reach the render). This is a heterogeneous registry boundary: it
  // points at fully-connected page components with wildly varied (and
  // redux-injected) prop shapes, so `any` is the honest element type here. A
  // narrower `ComponentType<Record<string, unknown>>` is contravariantly
  // *stricter* and rejects connected components whose accepted own-props are
  // all optional (the inferred shape of a converted page).
  component: ComponentType<any>;
  collapse?: boolean;
  hiddenChildRoutes?: boolean;
  views?: DashboardRoute[];
}

const DASHBOARD_LAYOUTS = ["/employer", "/workflow", "/individual", "/admin"];

export function renderDashboardRoutes(
  routes: DashboardRoute[]
): React.ReactElement[] {
  return routes.flatMap((route): React.ReactElement[] => {
    if (route.collapse || route.hiddenChildRoutes) {
      return renderDashboardRoutes(route.views || []);
    }
    if (!DASHBOARD_LAYOUTS.includes(route.layout)) {
      return [];
    }
    return [
      <Route
        key={route.path}
        path={route.path}
        element={<PrivateRoute path={route.path} component={route.component} />}
      />,
    ];
  });
}
